import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { usePopout, PopoutPlacement, useClickAway, useMeasure } from '@hooks';
import { WithChildrenCommonProps } from '@types';
import { twMerge } from 'tailwind-merge';
export interface IDropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Defines the width of the Dropdown element */
  dropdownWidth?: number | string;
  /**
   * Known height for positioning calculations.
   * Use when dropdown content has a known height (e.g., calendars)
   * to ensure correct positioning before content renders.
   */
  height?: number;
  /** Indicates whether the dropdown is opened */
  isOpen: boolean;
  /** Function to be executed when clicking outside the dropdown */
  onClickAway: VoidFunction;
  /** Indicates the position of the popout */
  placement?: PopoutPlacement;
  /** Reference of the element from which the dropdown is going to be shown */
  reference: HTMLElement | null;
}
export const Dropdown = t0 => {
  const $ = _c(40);
  let children;
  let classNameProp;
  let dropdownWidth;
  let height;
  let isOpen;
  let onClickAway;
  let reference;
  let rest;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      dropdownWidth,
      height,
      isOpen,
      onClickAway,
      placement: t1,
      reference,
      className: classNameProp,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = classNameProp;
    $[3] = dropdownWidth;
    $[4] = height;
    $[5] = isOpen;
    $[6] = onClickAway;
    $[7] = reference;
    $[8] = rest;
    $[9] = t1;
  } else {
    children = $[1];
    classNameProp = $[2];
    dropdownWidth = $[3];
    height = $[4];
    isOpen = $[5];
    onClickAway = $[6];
    reference = $[7];
    rest = $[8];
    t1 = $[9];
  }
  const placement = t1 === undefined ? "bottom" : t1;
  const t2 = !!dropdownWidth;
  let t3;
  if ($[10] !== height || $[11] !== isOpen || $[12] !== t2) {
    t3 = {
      enabled: isOpen,
      preventOverflow: t2,
      height
    };
    $[10] = height;
    $[11] = isOpen;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const {
    setPopoutElement,
    isPositioned
  } = usePopout(reference, placement, t3);
  const clickAwayParentRef = React.useRef(null);
  const [ref, t4] = useMeasure();
  const {
    width
  } = t4;
  let t5;
  let t6;
  if ($[14] !== ref || $[15] !== reference) {
    t5 = () => {
      reference && ref(reference);
    };
    t6 = [ref, reference];
    $[14] = ref;
    $[15] = reference;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  React.useEffect(t5, t6);
  let t7;
  if ($[18] !== isOpen || $[19] !== onClickAway) {
    t7 = () => {
      isOpen && onClickAway();
    };
    $[18] = isOpen;
    $[19] = onClickAway;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  useClickAway(clickAwayParentRef, t7);
  let t8;
  if ($[21] !== setPopoutElement) {
    t8 = element => {
      setPopoutElement(element);
      clickAwayParentRef.current = element;
    };
    $[21] = setPopoutElement;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const setRef = t8;
  if (!isOpen) {
    return null;
  }
  const shouldHideUntilPositioned = reference !== null && !isPositioned;
  const widthStyle = typeof dropdownWidth === "number" ? `${dropdownWidth}px` : dropdownWidth === "100%" || dropdownWidth === "auto" ? undefined : dropdownWidth || (width ? `${width}px` : undefined);
  const t9 = shouldHideUntilPositioned && "fixed opacity-0 pointer-events-none";
  let t10;
  if ($[23] !== classNameProp || $[24] !== t9) {
    t10 = twMerge("rounded-2xl bg-white border-[1.5px] border-solid border-primary-main overflow-hidden z-[1004]", t9, classNameProp);
    $[23] = classNameProp;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const tailwindClassName = t10;
  let t11;
  if ($[26] !== height) {
    t11 = height && {
      height: `${height}px`
    };
    $[26] = height;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11 || $[29] !== widthStyle) {
    t12 = {
      width: widthStyle,
      ...t11
    };
    $[28] = t11;
    $[29] = widthStyle;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const dropdownStyle = t12;
  let t13;
  if ($[31] !== shouldHideUntilPositioned) {
    t13 = shouldHideUntilPositioned ? {
      "aria-hidden": true as const,
      inert: true,
      tabIndex: -1
    } : {};
    $[31] = shouldHideUntilPositioned;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const accessibilityProps = t13;
  let t14;
  if ($[33] !== accessibilityProps || $[34] !== children || $[35] !== dropdownStyle || $[36] !== rest || $[37] !== setRef || $[38] !== tailwindClassName) {
    t14 = <div role="menu" ref={setRef} className={tailwindClassName} style={dropdownStyle} {...accessibilityProps} {...rest}>{children}</div>;
    $[33] = accessibilityProps;
    $[34] = children;
    $[35] = dropdownStyle;
    $[36] = rest;
    $[37] = setRef;
    $[38] = tailwindClassName;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  return t14;
};